import { useEffect, useState } from "react";
import { api, downloadFile, fmtBytes, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MonitorDown, Download, Package, Apple, Monitor, Terminal, Database, Cloud, Github, ExternalLink, Save } from "lucide-react";

const ICONS = { Windows: Monitor, macOS: Apple, Linux: Terminal, "Linux (deb)": Terminal };

export default function DesktopPanel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [repo, setRepo] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.desktopReleases().then((d) => { setData(d); setRepo(d.github?.repo || ""); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const grab = async (url, name) => {
    try { await downloadFile(`${API.replace(/\/api$/, "")}${url}`, name); }
    catch (e) { toast.error(e.message || "Download failed"); }
  };

  const saveRepo = async () => {
    setSaving(true);
    try {
      const g = await api.setGithubRepo(repo);
      setData((d) => ({ ...d, github: g }));
      toast[g.error ? "warning" : "success"](g.error || (g.repo ? `Linked to ${g.repo}` : "GitHub link cleared"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save repo");
    } finally { setSaving(false); }
  };

  const releases = data?.releases || [];
  const gh = data?.github || {};
  const ghAssets = gh.assets || [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[900px]" data-testid="desktop-panel">
      <header>
        <h2 className="font-mono font-extrabold text-2xl sm:text-3xl tracking-tight uppercase">Desktop App</h2>
        <p className="text-sm text-slate-500 mt-1">
          Run DataForge AI as a native window — pick your server and database on launch, and reach a local Ollama
          that a hosted browser can never touch.
        </p>
      </header>

      <div className="rounded-lg border border-border bg-[#0B0F18] p-6 space-y-4" data-testid="github-release-card">
        <div className="flex items-center gap-2">
          <Github className="h-4 w-4 text-cyan-400" />
          <h3 className="font-display font-bold text-base">Download from GitHub Releases</h3>
          {gh.tag && (
            <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-cyan-300 border border-cyan-500/40 bg-cyan-500/10 px-2 py-0.5 rounded">
              {gh.tag}
            </span>
          )}
        </div>

        {ghAssets.length > 0 ? (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="github-asset-list">
            {ghAssets.map((a) => {
              const Icon = ICONS[a.platform] || Package;
              return (
                <a key={a.name} href={a.url} data-testid={`github-download-${a.name}`}
                  className="group flex items-center gap-3 rounded-md border border-border bg-[#0E1420] px-4 py-3
                             hover:border-cyan-500/50 hover:bg-cyan-500/5 transition-colors">
                  <Icon className="h-5 w-5 text-cyan-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-mono text-xs text-slate-200 truncate">{a.name}</p>
                    <p className="font-mono text-[10px] text-slate-600">
                      {a.platform} · {fmtBytes(a.size_bytes)}{a.download_count ? ` · ${a.download_count} downloads` : ""}
                    </p>
                  </div>
                  <Download className="h-4 w-4 ml-auto text-slate-600 group-hover:text-cyan-300 transition-colors" />
                </a>
              );
            })}
          </div>
        ) : (
          <p className="text-xs font-mono text-slate-600" data-testid="github-empty-note">
            {gh.error ? gh.error
              : gh.repo ? "No installer assets on the latest release yet — push a tag like "
                : "Link your repository below, then push a tag like "}
            {!gh.error && <span className="text-cyan-300">desktop-v1.0.0</span>}
            {!gh.error && " and the workflow builds Windows, macOS and Linux installers into a GitHub Release."}
          </p>
        )}

        {gh.releases_url && (
          <a href={gh.releases_url} target="_blank" rel="noreferrer" data-testid="github-releases-link"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] text-slate-500 hover:text-cyan-300 transition-colors">
            <ExternalLink className="h-3 w-3" /> {gh.releases_url.replace("https://", "")}
          </a>
        )}

        {user?.role === "owner" && (
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end pt-3 border-t border-border">
            <div className="space-y-1.5">
              <Label className="text-[11px] text-slate-400">Repository</Label>
              <Input data-testid="github-repo-input" value={repo} onChange={(e) => setRepo(e.target.value)}
                placeholder="owner/repo or https://github.com/owner/repo"
                className="bg-[#0E1420] border-border font-mono text-sm" />
            </div>
            <Button data-testid="save-github-repo-button" onClick={saveRepo} disabled={saving}
              className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
              <Save className="h-3.5 w-3.5 mr-1.5" /> {saving ? "Saving" : "Save"}
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border bg-[#0B0F18] p-6 space-y-4">
        <div className="flex items-center gap-2">
          <MonitorDown className="h-4 w-4 text-cyan-400" />
          <h3 className="font-display font-bold text-base">Builds on this server</h3>
          <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-slate-600">v{data?.version || "1.0.0"}</span>
        </div>

        {releases.length === 0 ? (
          <p className="text-xs font-mono text-slate-600" data-testid="no-releases-note">
            No prebuilt installer on this server yet — grab the source bundle below and run
            <span className="text-slate-400"> npm install &amp;&amp; npm run dist </span>
            to produce a signed installer for your own OS.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="release-list">
            {releases.map((r) => {
              const Icon = ICONS[r.platform] || Package;
              return (
                <button key={r.name} data-testid={`download-${r.name}`} onClick={() => grab(r.url, r.name)}
                  className="group flex items-center gap-3 rounded-md border border-border bg-[#0E1420] px-4 py-3 text-left
                             hover:border-cyan-500/50 hover:bg-cyan-500/5 transition-colors">
                  <Icon className="h-5 w-5 text-cyan-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-mono text-xs text-slate-200 truncate">{r.name}</p>
                    <p className="font-mono text-[10px] text-slate-600">{r.platform} · {fmtBytes(r.size_bytes)}</p>
                  </div>
                  <Download className="h-4 w-4 ml-auto text-slate-600 group-hover:text-cyan-300 transition-colors" />
                </button>
              );
            })}
          </div>
        )}

        <Button data-testid="download-source-button" variant="outline"
          onClick={() => grab("/api/desktop/source.zip", "dataforge-desktop-source.zip")}
          className="border-border bg-[#0E1420] hover:bg-slate-800 font-mono text-xs">
          <Package className="h-3.5 w-3.5 mr-1.5" /> Download source bundle (.zip)
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-[#0B0F18] p-5 space-y-2">
          <div className="flex items-center gap-2">
            <Cloud className="h-4 w-4 text-cyan-400" />
            <h4 className="font-display font-bold text-sm">Hosted mode</h4>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            The desktop window points at this server, so you sign in with the same account and see the same crawls,
            datasets and files as the browser.
          </p>
        </div>
        <div className="rounded-lg border border-border bg-[#0B0F18] p-5 space-y-2">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-emerald-400" />
            <h4 className="font-display font-bold text-sm">Fully-local mode</h4>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            On first launch the app asks which server and which MongoDB to use. Point it at your own
            <span className="font-mono text-slate-400"> MONGO_URL </span> and
            <span className="font-mono text-slate-400"> DB_NAME </span>
            and every page you crawl stays on your machine.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-[#0B0F18] p-6 space-y-3">
        <h3 className="font-display font-bold text-base">Build it yourself</h3>
        <pre data-testid="desktop-build-instructions"
          className="rounded-md border border-border bg-[#04060A] p-3 font-mono text-[11px] text-cyan-300 overflow-x-auto">{`unzip dataforge-desktop-source.zip && cd dataforge-desktop
npm install
npm start                 # run it now
npm run dist:win          # Windows installer (.exe)
npm run dist:mac          # macOS disk image (.dmg)
npm run dist:linux        # Linux AppImage + .deb`}</pre>
        <p className="text-[11px] font-mono text-slate-600">
          Installers must be built on (or cross-compiled for) the target OS — macOS builds require a Mac.
        </p>
      </div>
    </div>
  );
}
